import { useState, type FormEvent } from 'react';
import type { SelfVoiceData } from '@shared/types';
import { selfApi } from './selfApi';
import { useT, type Messages } from './i18n/LanguageProvider';
import LangToggle from './LangToggle';
import styles from './Self.module.css';

interface Props {
  token: string;
  onSuccess: (data: SelfVoiceData) => void;
}

// Fehlercode (aus dem Body) → verständliche Meldung in der gewählten Sprache.
function messageFor(code: string, t: Messages): string {
  if (code === 'invalid') return t.loginErrorInvalid;
  if (code === 'too_many_attempts') return t.loginErrorTooMany;
  return t.loginErrorGeneric;
}

export default function SelfLogin({ token, onSuccess }: Props) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // Gemerkt wird der Fehlercode, nicht der Text — sonst bliebe eine Meldung nach
  // dem Umschalten in der alten Sprache stehen.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const { t } = useT();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setErrorCode(null);
    try {
      await selfApi.login(token, password);
      // Login setzt das Session-Cookie → Daten laden und in die Edit-Ansicht.
      const data = await selfApi.getData(token);
      onSuccess(data);
    } catch (err) {
      setErrorCode(err instanceof Error ? err.message : '');
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.langBar}>
        <LangToggle />
      </div>
      <div className={styles.loginWrap}>
        <form className={styles.loginCard} onSubmit={submit}>
          <img src="/brand/tro_logo_pos.svg" alt="TRO" className={styles.loginLogo} />
          <h1 className={styles.loginTitle}>{t.profileTitle}</h1>
          <p className={styles.loginHint}>{t.loginHint}</p>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="self-pw">{t.passwordLabel}</label>
            <input
              id="self-pw"
              className={styles.input}
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>
          {errorCode !== null && <p className={styles.msgError}>{messageFor(errorCode, t)}</p>}
          <button type="submit" className={styles.btnPrimary} disabled={busy || !password}>
            {busy ? t.loginBusy : t.loginButton}
          </button>
        </form>
      </div>
    </div>
  );
}
